import {
  CTCSS_TONES,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 0x10
const MEMORY_SIZE = 0x8000
const FLAGS_START = 0x0100
const CHANNEL_START = 0x2000
const CHANNEL_SIZE = 32
const CHANNEL_COUNT = 758
const TONES = [62.5, ...CTCSS_TONES]
const TMODES = ['', 'Tone', 'DTCS', ''] as const
const DUPLEXES = ['', '-', '+', 'off'] as const
const MODES = ['FM', 'FM', 'NFM'] as const
const POWER_LEVELS = ['High', 'Mid1', 'Mid2', 'Low'] as const
const TUNING_STEPS = [2.5, 5, 6.25, 10, 12.5, 15, 20, 25, 30, 50]

type AnyTone5888Spec = {
  id: string
  vendor: string
  model: string
  summary: string
  radioType: string
}

export type AnyTone5888Profile = {
  protocol: 'anytone5888'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: number
  nameLength: 7
  memorySize: number
  radioType: string
  powerLevels: readonly string[]
}

const SPECS: readonly AnyTone5888Spec[] = [
  {
    id: 'anytone-5888uv',
    vendor: 'AnyTone',
    model: '5888UV',
    summary: 'AnyTone 5888UV dual-band mobile with 758 memory channels.',
    radioType: 'QX588UV',
  },
  {
    id: 'intek-hr-2040',
    vendor: 'Intek',
    model: 'HR-2040',
    summary: 'Intek HR-2040 dual-band mobile in the AnyTone 5888UV family.',
    radioType: 'HR-2040',
  },
  {
    id: 'polmar-db-50m',
    vendor: 'Polmar',
    model: 'DB-50M',
    summary: 'Polmar DB-50M dual-band mobile in the AnyTone 5888UV family.',
    radioType: 'DB-50M',
  },
  {
    id: 'powerwerx-db-750x',
    vendor: 'Powerwerx',
    model: 'DB-750X',
    summary: 'Powerwerx DB-750X dual-band mobile in the AnyTone 5888UV family.',
    radioType: 'DB-750X',
  },
]

export const ANYTONE_5888_PROFILES: readonly AnyTone5888Profile[] = SPECS.map(
  (spec) => ({
    protocol: 'anytone5888',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'Read + write', '758 channels', '7-character names'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 7,
    memorySize: MEMORY_SIZE,
    radioType: spec.radioType,
    powerLevels: POWER_LEVELS,
  }),
)

function emptyChannel(number: number): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: 'High',
    skip: false,
  }
}

function decodeBcd(raw: Uint8Array): number {
  let value = 0
  for (const byte of raw) {
    value = value * 100 + (byte >> 4) * 10 + (byte & 0x0f)
  }
  return value
}

function encodeBcd(value: number, size: number): Uint8Array {
  const result = new Uint8Array(size)
  let remaining = Math.max(0, Math.trunc(value))
  for (let index = size - 1; index >= 0; index -= 1) {
    const part = remaining % 100
    result[index] = (Math.floor(part / 10) << 4) | (part % 10)
    remaining = Math.floor(remaining / 100)
  }
  return result
}

function allDcsCode(index: number): number {
  return Number(index.toString(8).padStart(3, '0'))
}

function allDcsIndex(code: number): number {
  const text = code.toString()
  return /^[0-7]{3}$/.test(text) ? Number.parseInt(text, 8) : -1
}

function flagNibble(image: Uint8Array, number: number): number {
  const byte = image[FLAGS_START + Math.floor((number - 1) / 2)]
  return number % 2 ? byte & 0x0f : (byte >> 4) & 0x0f
}

function writeFlagNibble(image: Uint8Array, number: number, value: number): void {
  const offset = FLAGS_START + Math.floor((number - 1) / 2)
  image[offset] = number % 2
    ? (image[offset] & 0xf0) | (value & 0x0f)
    : (image[offset] & 0x0f) | ((value & 0x0f) << 4)
}

function decodeName(raw: Uint8Array): string {
  return String.fromCharCode(
    ...raw.map((value) => (value === 0xff || value === 0 ? 0x20 : value)),
  ).trim()
}

export function parseAnyTone5888Channel(
  image: Uint8Array,
  profile: AnyTone5888Profile,
  number: number,
): WebChannel {
  if (number < 1 || number > CHANNEL_COUNT) {
    throw new Error('Channel must be between 1 and 758.')
  }
  const flag = flagNibble(image, number)
  if (flag === 0x0f) return emptyChannel(number)
  const start = CHANNEL_START + (number - 1) * CHANNEL_SIZE
  const raw = image.slice(start, start + CHANNEL_SIZE)
  if (raw.byteLength !== CHANNEL_SIZE) return emptyChannel(number)
  let rxFrequency = decodeBcd(raw.slice(0, 4)) * 100
  const lastDigit = decodeBcd(raw.slice(0, 4)) % 10
  if (lastDigit === 2 || lastDigit === 7) rxFrequency += 50
  if (!rxFrequency) return emptyChannel(number)
  const offset = decodeBcd(raw.slice(4, 8)) * 100
  const flags = raw[9]
  const powerFlags = raw[10]
  const txOff = (flags & 0x01) !== 0
  const duplex = powerFlags & 0x03
  const txFrequency = txOff
    ? null
    : duplex === 1
      ? rxFrequency - offset
      : duplex === 2
        ? rxFrequency + offset
        : rxFrequency

  const txToneMode = TMODES[raw[11] & 0x03]
  const rxToneMode = TMODES[(raw[11] >> 2) & 0x03]
  const txDcsIndex = raw[14] | (((flags >> 6) & 0x01) << 8)
  const rxDcsIndex = raw[15] | (((flags >> 7) & 0x01) << 8)
  const selectedTone =
    txToneMode === 'Tone'
      ? {
          toneMode: 'ctcss' as const,
          ctcss: TONES[raw[12] & 0x3f] ?? null,
          dtcs: null,
        }
      : txToneMode === 'DTCS'
        ? {
            toneMode: 'dtcs' as const,
            ctcss: null,
            dtcs: allDcsCode(txDcsIndex),
          }
        : rxToneMode === 'Tone'
          ? {
              toneMode: 'ctcss' as const,
              ctcss: TONES[raw[13] & 0x3f] ?? null,
              dtcs: null,
            }
          : rxToneMode === 'DTCS'
            ? {
                toneMode: 'dtcs' as const,
                ctcss: null,
                dtcs: allDcsCode(rxDcsIndex),
              }
            : { toneMode: 'none' as const, ctcss: null, dtcs: null }
  const dcsReverse =
    selectedTone.toneMode === 'dtcs' &&
    ((txToneMode === 'DTCS' && (flags & 0x10) !== 0) ||
      (txToneMode !== 'DTCS' && (flags & 0x20) !== 0))

  return {
    number,
    empty: false,
    name: decodeName(raw.slice(23, 30)),
    rxFrequency,
    txFrequency,
    ...selectedTone,
    dtcsPolarity: dcsReverse ? 'R' : 'N',
    mode: powerFlags & 0x10 ? 'AM' : MODES[(flags >> 2) & 0x03] ?? 'FM',
    power: POWER_LEVELS[(powerFlags >> 2) & 0x03] ?? 'High',
    skip: (flag & 0x03) !== 0,
    tuningStep: TUNING_STEPS[raw[8] & 0x0f] ?? 5,
  }
}

export function parseAnyTone5888Channels(
  image: Uint8Array,
  profile: AnyTone5888Profile,
): WebChannel[] {
  return Array.from({ length: CHANNEL_COUNT }, (_, index) =>
    parseAnyTone5888Channel(image, profile, index + 1),
  )
}

export function applyAnyTone5888Channel(
  image: Uint8Array,
  profile: AnyTone5888Profile,
  channel: WebChannel,
): void {
  if (channel.number < 1 || channel.number > CHANNEL_COUNT) {
    throw new Error('Channel must be between 1 and 758.')
  }
  if (channel.empty) {
    writeFlagNibble(image, channel.number, 0x0f)
    return
  }
  const flagValue = channel.skip ? 0x01 : 0
  writeFlagNibble(image, channel.number, flagValue)
  const start = CHANNEL_START + (channel.number - 1) * CHANNEL_SIZE
  const raw = image.subarray(start, start + CHANNEL_SIZE)
  raw.fill(0)
  const rx = channel.rxFrequency
  if (!rx) throw new Error('A receive frequency is required.')
  raw.set(encodeBcd(Math.round(rx / 100), 4), 0)
  const tx = channel.txFrequency
  const offset = tx ? Math.abs(tx - rx) : 0
  raw.set(encodeBcd(Math.round(offset / 100), 4), 4)
  const stepIndex = TUNING_STEPS.indexOf(channel.tuningStep ?? 5)
  raw[8] = Math.max(0, stepIndex) & 0x0f
  const duplex = tx ? (tx > rx ? 2 : tx < rx ? 1 : 0) : 0
  raw[9] = tx ? 0 : 0x01
  raw[9] |= channel.mode === 'NFM' ? 0x08 : 0
  raw[10] =
    (channel.mode === 'AM' ? 0x10 : 0) |
    ((Math.max(0, POWER_LEVELS.indexOf(channel.power as (typeof POWER_LEVELS)[number])) & 0x03) << 2) |
    (duplex & 0x03)
  if (channel.toneMode === 'ctcss') {
    const index = (TONES as readonly number[]).indexOf(channel.ctcss ?? -1)
    if (index < 0) throw new Error('That CTCSS tone is not supported.')
    raw[11] = 0x01 | (0x01 << 2)
    raw[12] = index
    raw[13] = index
    raw[18] = 1
  } else if (channel.toneMode === 'dtcs') {
    const index = allDcsIndex(channel.dtcs ?? -1)
    if (index < 0) throw new Error('That DCS code is not supported.')
    raw[11] = 0x02 | (0x02 << 2)
    raw[14] = index & 0xff
    raw[15] = index & 0xff
    raw[18] = 1
    if (index > 0xff) raw[9] |= 0xc0
    if (channel.dtcsPolarity === 'R') raw[9] |= 0x30
  }
  const name = channel.name
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 7)
    .padEnd(7, ' ')
  for (let index = 0; index < 7; index += 1) {
    raw[23 + index] = name.charCodeAt(index)
  }
}

function checksum(data: Uint8Array): number {
  let total = 0
  for (const value of data) total = (total + value) & 0xff
  return total
}

async function echoWrite(
  transport: RadioTransport,
  data: Uint8Array,
): Promise<void> {
  await transport.write(data)
  await transport.readExact(data.length, 3000)
}

async function enter(
  profile: AnyTone5888Profile,
  transport: RadioTransport,
): Promise<void> {
  await transport.discardInput()
  const program = new TextEncoder().encode('PROGRAM')
  await echoWrite(transport, program)
  const response = await transport.readExact(3, 2500)
  if (response[0] !== 0x51 || response[1] !== 0x58 || response[2] !== ACK) {
    throw new Error('The radio refused AnyTone 5888 programming mode.')
  }
  await echoWrite(transport, Uint8Array.of(0x02))
  const identity = await transport.readExact(16, 3000)
  const model = String.fromCharCode(
    ...identity.slice(1, 8).filter((value) => value !== 0),
  ).trim()
  if (model !== profile.radioType) {
    throw new Error(
      `The connected radio identifies as ${model || 'unknown'}, not ${profile.model}.`,
    )
  }
}

function readCommand(address: number): Uint8Array {
  return Uint8Array.of(
    0x52,
    (address >>> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  )
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await echoWrite(transport, readCommand(address))
  const response = await transport.readExact(BLOCK_SIZE + 6, 4000)
  if (
    response[3] !== BLOCK_SIZE ||
    response.at(-1) !== ACK ||
    checksum(response.slice(1, BLOCK_SIZE + 4)) !== response[BLOCK_SIZE + 4]
  ) {
    throw new Error(`Invalid AnyTone 5888 block at 0x${address.toString(16)}.`)
  }
  return response.slice(4, 4 + BLOCK_SIZE)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(6 + BLOCK_SIZE)
  frame.set([
    0x57,
    (address >>> 8) & 0xff,
    address & 0xff,
    BLOCK_SIZE,
  ])
  frame.set(data, 4)
  frame[BLOCK_SIZE + 4] = checksum(frame.slice(1, BLOCK_SIZE + 4))
  frame[BLOCK_SIZE + 5] = ACK
  await echoWrite(transport, frame)
  if ((await transport.readExact(1, 3000))[0] !== ACK) {
    throw new Error(`The radio rejected block 0x${address.toString(16)}.`)
  }
}

async function finish(transport: RadioTransport): Promise<void> {
  const end = new TextEncoder().encode('END')
  await echoWrite(transport, end)
  if ((await transport.readExact(1, 2000))[0] !== ACK) {
    throw new Error('The radio did not exit programming mode cleanly.')
  }
}

export async function readAnyTone5888RadioWithTransport(
  profile: AnyTone5888Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enter(profile, transport)
  try {
    const image = new Uint8Array(MEMORY_SIZE)
    const total = MEMORY_SIZE / BLOCK_SIZE
    for (let index = 0; index < total; index += 1) {
      const address = index * BLOCK_SIZE
      image.set(await readBlock(transport, address), address)
      onProgress?.({
        current: index + 1,
        total,
        label: 'Reading AnyTone 5888 memory',
      })
    }
    return { image, reportedModel: profile.model }
  } finally {
    await finish(transport)
  }
}

function usedLocation(image: Uint8Array, address: number): boolean {
  if (address < CHANNEL_START || address >= 0x7ec0) return true
  const location = Math.floor((address - CHANNEL_START) / CHANNEL_SIZE)
  return image[FLAGS_START + Math.floor(location / 2)] !== 0xff
}

export async function writeAnyTone5888RadioWithTransport(
  profile: AnyTone5888Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < MEMORY_SIZE) {
    throw new Error('The selected image is smaller than AnyTone 5888 memory.')
  }
  await enter(profile, transport)
  try {
    const addresses = Array.from(
      { length: MEMORY_SIZE / BLOCK_SIZE },
      (_, index) => index * BLOCK_SIZE,
    ).filter(
      (address) => address >= FLAGS_START && usedLocation(image, address),
    )
    for (let index = 0; index < addresses.length; index += 1) {
      const address = addresses[index]
      await writeBlock(
        transport,
        address,
        image.slice(address, address + BLOCK_SIZE),
      )
      onProgress?.({
        current: index + 1,
        total: addresses.length,
        label: 'Writing AnyTone 5888 memory',
      })
    }
  } finally {
    await finish(transport)
  }
}
